import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { data, redirect, Form, Link, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getAccessGrantByCode } from '#app/utils/access-codes.server.ts'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ASSESSMENT_NAME_SHORT,
	SITE_NAME,
} from '#app/utils/branding.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { SignupVerificationEmail } from '#app/utils/email-templates.tsx'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getClientIp } from '#app/utils/http.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import {
	checkAnonymousRateLimit,
	SIGNUP_RATE_LIMIT,
} from '#app/utils/rate-limit.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { type Route } from './+types/signup.ts'
import { onboardingEmailSessionKey } from './onboarding/index.tsx'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z.object({
	email: EmailSchema,
})

function parseCampaignSignupRedirect(redirectTo: string | undefined) {
	if (!redirectTo) return null
	if (!redirectTo.startsWith('/')) return null

	const redirectUrl = new URL(redirectTo, 'http://localhost')
	if (redirectUrl.origin !== 'http://localhost') return null

	const [, route, code, ...extraSegments] = redirectUrl.pathname.split('/')
	if (route !== 'redeem' || !code || extraSegments.length > 0) return null
	if (!/^[a-z0-9-]+$/i.test(code)) return null

	return {
		code,
		redirectTo: `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`,
	}
}

async function getVerifiedCampaignSignupRedirect(
	redirectTo: string | undefined,
) {
	const campaignRedirect = parseCampaignSignupRedirect(redirectTo)
	if (!campaignRedirect) return null

	const accessGrant = await getAccessGrantByCode(campaignRedirect.code)
	if (!accessGrant) return null

	return campaignRedirect.redirectTo
}

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return null
}

export async function action({ request }: Route.ActionArgs) {
	// Check IP-based rate limit first to prevent bot abuse
	const clientIp = getClientIp(request)
	const rateLimitResult = await checkAnonymousRateLimit({
		ip: clientIp,
		endpoint: 'signup',
		config: SIGNUP_RATE_LIMIT,
	})

	if (!rateLimitResult.allowed) {
		return data(
			{
				result: {
					status: 'error' as const,
					initialValue: {},
					value: null,
					error: {
						'': [
							`Too many signup attempts. Please try again in ${Math.ceil((rateLimitResult.retryAfter ?? 3600) / 60)} minutes.`,
						],
					},
				},
			},
			{
				status: 429,
				headers: {
					'Retry-After': String(rateLimitResult.retryAfter ?? 3600),
				},
			},
		)
	}

	const formData = await request.formData()

	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email } = submission.value
	const url = new URL(request.url)
	const redirectTo = url.searchParams.get('redirectTo') ?? undefined
	const campaignSignupRedirectTo =
		await getVerifiedCampaignSignupRedirect(redirectTo)
	if (campaignSignupRedirectTo) {
		const verifySession = await verifySessionStorage.getSession()
		verifySession.set(onboardingEmailSessionKey, email)

		const onboardingUrl = new URL('/onboarding', 'http://localhost')
		onboardingUrl.searchParams.set('redirectTo', campaignSignupRedirectTo)

		return redirect(onboardingUrl.pathname + onboardingUrl.search, {
			headers: {
				'set-cookie': await verifySessionStorage.commitSession(verifySession),
			},
		})
	}

	const { verifyUrl, redirectTo: verifyRedirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
		redirectTo,
	})

	const response = await sendEmail({
		to: email,
		subject: `Welcome to ${SITE_NAME}`,
		semantics: 'account-critical',
		react: (
			<SignupVerificationEmail
				onboardingUrl={verifyUrl.toString()}
				otp={otp}
			/>
		),
	})

	if (response.status === 'success') {
		return redirect(verifyRedirectTo.toString())
	} else {
		return data(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export const meta: Route.MetaFunction = () => {
	return [
		{ title: `Sign Up - ${SITE_NAME}` },
		{
			name: 'description',
			content: `Create your ${SITE_NAME} account to continue to checkout and begin ${ASSESSMENT_NAME_SHORT}.`,
		},
	]
}

export default function SignupRoute({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<main
			className="flex min-h-screen flex-col justify-center px-6 py-16 bg-cathedral-page"
		>
			<div className="mx-auto w-full max-w-md rounded-xl border bg-cathedral-card p-8 md:p-10">
				<div className="text-center">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.14em] uppercase">
						{SITE_NAME}
					</p>
					<h1 className="cathedral-heading text-cathedral-dark mt-2 text-4xl font-semibold tracking-wide">
						Begin Your Journey
					</h1>
					<p className="cathedral-body text-cathedral-brown mt-3 text-lg">
						Create your account to continue.
					</p>
					{redirectTo ? (
						<p className="cathedral-body text-cathedral-brown mt-2 text-sm" aria-live="polite">
							You'll continue where you left off after signup.
						</p>
					) : null}
				</div>
				<div className="mx-auto mt-10 max-w-sm min-w-full sm:min-w-[368px]">
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<Field
							labelProps={{
								htmlFor: fields.email.id,
								children: 'Email',
								className: 'text-cathedral-brown',
							}}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								autoFocus: true,
								autoComplete: 'email',
							}}
							errors={fields.email.errors}
						/>
						<ErrorList errors={form.errors} id={form.errorId} />
						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Continue
						</StatusButton>
					</Form>
					<ul className="flex flex-col gap-4 py-4">
						{providerNames.map((providerName) => (
							<li key={providerName}>
								<hr className="mb-4 border-bronze-medium" />
								<ProviderConnectionForm
									type="Signup"
									providerName={providerName}
									redirectTo={redirectTo}
									className="min-h-[44px] rounded-sm border border-cathedral-gold/40 !bg-cathedral-surface-strong px-4 py-3 cathedral-heading text-sm tracking-[0.12em] !text-cathedral-dark uppercase transition-colors hover:border-cathedral-gold hover:!bg-cathedral-surface hover:!text-cathedral-gold"
								/>
							</li>
						))}
					</ul>
					<p className="cathedral-body text-cathedral-brown pt-2 text-center text-sm">
						Already have an account?{' '}
						<Link
							to={
								redirectTo
									? `/login?redirectTo=${encodeURIComponent(redirectTo)}`
									: '/login'
							}
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Log in
						</Link>
					</p>
					<p className="cathedral-body text-cathedral-brown pt-2 text-center text-xs">
						By continuing, you agree to our{' '}
						<Link
							to="/tos"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Terms of Service
						</Link>{' '}
						and{' '}
						<Link
							to="/privacy"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Privacy Policy
						</Link>
						.
					</p>
				</div>
			</div>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
